/**
 * New Deal Room creation page.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealRoomService from '../services/dealRoomService';
import dealPipelineService from '../services/dealPipelineService';
import projectService from '../services/projectService';
import { AccessTierMap, DealRoomStatus } from '../types/dealRoom';
import { DealPipeline } from '../types/dealPipeline';
import { Project } from '../types/project';

const defaultTiers: AccessTierMap = {
  basic: ['pitch_deck', 'executive_summary'],
  qualified: ['financials', 'cap_table', 'product_demo'],
  lead: ['legal_docs', 'ip_docs', 'references'],
};

const toCsv = (arr: string[]): string => arr.join(', ');
const fromCsv = (s: string): string[] =>
  s
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

const NewDealRoomPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledPipelineId = searchParams.get('pipeline_id');

  const [roomName, setRoomName] = useState('');
  const [pipelineId, setPipelineId] = useState<number | ''>(
    prefilledPipelineId ? Number(prefilledPipelineId) : ''
  );
  const [startupId, setStartupId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [status, setStatus] = useState<DealRoomStatus>('draft');
  const [basicCsv, setBasicCsv] = useState<string>(toCsv(defaultTiers.basic));
  const [qualifiedCsv, setQualifiedCsv] = useState<string>(
    toCsv(defaultTiers.qualified)
  );
  const [leadCsv, setLeadCsv] = useState<string>(toCsv(defaultTiers.lead));
  const [ndaRequired, setNdaRequired] = useState(true);
  const [ndaTemplateUrl, setNdaTemplateUrl] = useState('');

  const [pipelines, setPipelines] = useState<DealPipeline[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [pipelineRes, projectRes] = await Promise.all([
        dealPipelineService.fetchPipelines({ size: 200 }),
        projectService.fetchProjects({ size: 200 }),
      ]);
      if (pipelineRes.data) setPipelines(pipelineRes.data.pipelines);
      if (projectRes.data) setProjects(projectRes.data.projects);
    };
    load();
  }, []);

  // Auto-fill startup/project from pipeline selection
  useEffect(() => {
    if (pipelineId === '') return;
    const p = pipelines.find((x) => x.id === pipelineId);
    if (p) {
      if (p.project_id) setProjectId(p.project_id);
      if (p.startup_id) setStartupId(p.startup_id);
      if (!roomName) {
        const target =
          p.project?.project_name ||
          p.startup?.name ||
          `Pipeline ${p.id}`;
        setRoomName(`${target} Deal Room`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId, pipelines]);

  const submit = async () => {
    if (!roomName.trim()) {
      toast.error('Room name is required');
      return;
    }
    setSubmitting(true);
    const res = await dealRoomService.createDealRoom({
      room_name: roomName.trim(),
      pipeline_id: pipelineId === '' ? null : pipelineId,
      startup_id: startupId === '' ? null : startupId,
      project_id: projectId === '' ? null : projectId,
      status,
      access_tiers: {
        basic: fromCsv(basicCsv),
        qualified: fromCsv(qualifiedCsv),
        lead: fromCsv(leadCsv),
      },
      nda_required: ndaRequired,
      nda_template_url: ndaTemplateUrl.trim() || null,
    });
    setSubmitting(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      toast.success('Deal room created');
      navigate(`/deal-rooms/${res.data.id}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <Link
            to="/deal-rooms"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to deal rooms
          </Link>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Deal Room</h1>
          <p className="text-sm text-gray-600 mt-1">
            Shared, tiered-access space for documents, NDAs, and investor
            engagement.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Room name
            </label>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Acme Series A Data Room"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Pipeline
              </label>
              <select
                value={pipelineId}
                onChange={(e) =>
                  setPipelineId(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">None</option>
                {pipelines.map((p) => {
                  const inv =
                    p.investor?.full_name || `Investor #${p.investor_id}`;
                  const target =
                    p.project?.project_name ||
                    p.startup?.name ||
                    `Pipeline #${p.id}`;
                  return (
                    <option key={p.id} value={p.id}>
                      #{p.id} · {inv} → {target}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Project
              </label>
              <select
                value={projectId}
                onChange={(e) =>
                  setProjectId(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">None</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Startup ID
              </label>
              <input
                type="number"
                value={startupId}
                onChange={(e) =>
                  setStartupId(
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as DealRoomStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Access tiers
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Comma-separated file types visible at each tier. Higher tiers
              typically include lower tiers' content.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Basic</label>
                <input
                  type="text"
                  value={basicCsv}
                  onChange={(e) => setBasicCsv(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Qualified
                </label>
                <input
                  type="text"
                  value={qualifiedCsv}
                  onChange={(e) => setQualifiedCsv(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Lead</label>
                <input
                  type="text"
                  value={leadCsv}
                  onChange={(e) => setLeadCsv(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={ndaRequired}
                onChange={(e) => setNdaRequired(e.target.checked)}
              />
              Require NDA before access
            </label>

            <div className="mt-3">
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                NDA template URL
              </label>
              <input
                type="url"
                value={ndaTemplateUrl}
                onChange={(e) => setNdaTemplateUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Link
              to="/deal-rooms"
              className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Cancel
            </Link>
            <button
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Create
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NewDealRoomPage;
